import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { executeProfilePlaybook, loginToGateway, SESSION_TOKEN_KEY } from '../services/api';
import { getFreshIdToken } from '../services/freshIdToken';
import {
    EMPTY_PROFILE,
    DEFAULT_PREFERENCES,
    DEFAULT_NOTIFICATIONS,
} from '../types/profile';
import type {
    ActivityEntry,
    Booking,
    NotificationSettings,
    ProfileState,
    SavedTrip,
    TravelPreferences,
} from '../types/profile';

const MAX_ACTIVITY = 50;

const newId = (prefix: string) =>
    `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

/** Convert Firestore REST/MCP typed values to the plain profile shape. */
const decodeStoredValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(decodeStoredValue);
  if (!isRecord(value)) return value;

  if ('stringValue' in value) return value.stringValue;
  if ('integerValue' in value) return Number(value.integerValue);
  if ('doubleValue' in value) return Number(value.doubleValue);
  if ('booleanValue' in value) return value.booleanValue;
  if ('timestampValue' in value) return value.timestampValue;
  if ('nullValue' in value) return null;
  if (isRecord(value.mapValue) && isRecord(value.mapValue.fields)) {
    return decodeStoredValue(value.mapValue.fields);
  }
  if (isRecord(value.arrayValue) && Array.isArray(value.arrayValue.values)) {
    return value.arrayValue.values.map(decodeStoredValue);
  }
  if (isRecord(value.fields)) return decodeStoredValue(value.fields);

  return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, decodeStoredValue(nested)]));
};

/**
 * Merge stored state over defaults so a profile saved by an older build
 * still loads when new preference fields are added.
 *
 * Also mints a guest `userId` when one is missing (brand-new profile, or one
 * stored before `userId` existed) so the shape is always valid even before
 * Auth0 has resolved. `ProfileProvider`'s effect overwrites this with the
 * real Auth0 subject as soon as a login is confirmed — this is just the
 * offline-first placeholder until then.
 */
const hydrate = (value: unknown, fallbackUserId = ''): ProfileState => {
  const decoded = decodeStoredValue(value);
  const raw = decoded && typeof decoded === 'object' ? decoded as Partial<ProfileState> & Record<string, unknown> : {};
  const rawPreferences: Record<string, unknown> = isRecord(raw.preferences) ? raw.preferences : {};
  const rawNotifications: Record<string, unknown> = isRecord(raw.notifications) ? raw.notifications : {};
  const normalizeSavedTrips = (value: unknown) => Array.isArray(value)
    ? value.map(item => {
      if (!isRecord(item)) return item;
      return {
        ...item,
        userId: item.userId ?? item.user_id,
        savedAt: item.savedAt ?? item.saved_at,
      };
    })
    : value;
  const normalizeBookings = (value: unknown) => Array.isArray(value)
    ? value.map(item => {
      if (!isRecord(item)) return item;
      return {
        ...item,
        userId: item.userId ?? item.user_id,
        startDate: item.startDate ?? item.start_date,
        endDate: item.endDate ?? item.end_date,
      };
    })
    : value;
  const normalizeActivity = (value: unknown) => Array.isArray(value)
    ? value.map(item => {
      if (!isRecord(item)) return item;
      return { ...item, at: item.at ?? item.created_at };
    })
    : value;
  const parsed = {
    ...raw,
    userId: raw.userId ?? raw.user_id ?? raw.uid,
    displayName: raw.displayName ?? raw.display_name ?? EMPTY_PROFILE.displayName,
    homeCity: raw.homeCity ?? raw.home_city ?? EMPTY_PROFILE.homeCity,
    bookings: normalizeBookings(raw.bookings ?? raw.bookings_list),
    savedTrips: normalizeSavedTrips(raw.savedTrips ?? raw.saved_trips),
    activity: normalizeActivity(raw.activity),
    preferences: {
      ...rawPreferences,
      homeAirport: rawPreferences.homeAirport ?? rawPreferences.home_airport ?? DEFAULT_PREFERENCES.homeAirport,
      preferredAirlines: rawPreferences.preferredAirlines ?? rawPreferences.preferred_airlines ?? DEFAULT_PREFERENCES.preferredAirlines,
      avoidedAirlines: rawPreferences.avoidedAirlines ?? rawPreferences.avoided_airlines ?? DEFAULT_PREFERENCES.avoidedAirlines,
      maxStops: rawPreferences.maxStops ?? rawPreferences.max_stops ?? DEFAULT_PREFERENCES.maxStops,
      hotelAmenities: rawPreferences.hotelAmenities ?? rawPreferences.hotel_amenities ?? DEFAULT_PREFERENCES.hotelAmenities,
      propertyTypes: rawPreferences.propertyTypes ?? rawPreferences.property_types ?? DEFAULT_PREFERENCES.propertyTypes,
      minHotelRating: rawPreferences.minHotelRating ?? rawPreferences.min_hotel_rating ?? DEFAULT_PREFERENCES.minHotelRating,
      budgetPerNightMin: rawPreferences.budgetPerNightMin ?? rawPreferences.budget_per_night_min ?? DEFAULT_PREFERENCES.budgetPerNightMin,
      budgetPerNightMax: rawPreferences.budgetPerNightMax ?? rawPreferences.budget_per_night_max ?? DEFAULT_PREFERENCES.budgetPerNightMax,
      favoriteDestinations: rawPreferences.favoriteDestinations ?? rawPreferences.favorite_destinations ?? DEFAULT_PREFERENCES.favoriteDestinations,
      dietaryNeeds: rawPreferences.dietaryNeeds ?? rawPreferences.dietary_needs ?? DEFAULT_PREFERENCES.dietaryNeeds,
      travellingWithKids: rawPreferences.travellingWithKids ?? rawPreferences.travelling_with_kids ?? DEFAULT_PREFERENCES.travellingWithKids,
      accessibilityNeeds: rawPreferences.accessibilityNeeds ?? rawPreferences.accessibility_needs ?? DEFAULT_PREFERENCES.accessibilityNeeds,
    },
    notifications: {
      ...rawNotifications,
      priceDropAlerts: rawNotifications.priceDropAlerts ?? rawNotifications.price_drop_alerts ?? DEFAULT_NOTIFICATIONS.priceDropAlerts,
      bookingReminders: rawNotifications.bookingReminders ?? rawNotifications.booking_reminders ?? DEFAULT_NOTIFICATIONS.bookingReminders,
      weeklyInspiration: rawNotifications.weeklyInspiration ?? rawNotifications.weekly_inspiration ?? DEFAULT_NOTIFICATIONS.weeklyInspiration,
      productUpdates: rawNotifications.productUpdates ?? rawNotifications.product_updates ?? DEFAULT_NOTIFICATIONS.productUpdates,
    },
  } as Partial<ProfileState>;
  return {
    ...EMPTY_PROFILE,
    ...parsed,
    // Auth0's stable subject must override stale identity stored in the profile.
    userId: fallbackUserId || parsed.userId || '',
    preferences: { ...DEFAULT_PREFERENCES, ...(parsed.preferences || {}) },
    notifications: { ...DEFAULT_NOTIFICATIONS, ...(parsed.notifications || {}) },
    bookings: Array.isArray(parsed.bookings) ? parsed.bookings : [],
    savedTrips: Array.isArray(parsed.savedTrips) ? parsed.savedTrips : [],
    activity: Array.isArray(parsed.activity) ? parsed.activity : [],
  };
};

const toPlaybookProfile = (value: ProfileState): Record<string, unknown> => ({
  ...value,
  user_id: value.userId,
  display_name: value.displayName,
  home_city: value.homeCity,
  saved_trips: value.savedTrips.map(trip => ({
    ...trip,
    user_id: trip.userId,
    saved_at: trip.savedAt,
  })),
  preferences: {
    ...value.preferences,
    home_airport: value.preferences.homeAirport,
    preferred_airlines: value.preferences.preferredAirlines,
    avoided_airlines: value.preferences.avoidedAirlines,
    max_stops: value.preferences.maxStops,
    hotel_amenities: value.preferences.hotelAmenities,
    property_types: value.preferences.propertyTypes,
    min_hotel_rating: value.preferences.minHotelRating,
    budget_per_night_min: value.preferences.budgetPerNightMin,
    budget_per_night_max: value.preferences.budgetPerNightMax,
    favorite_destinations: value.preferences.favoriteDestinations,
    dietary_needs: value.preferences.dietaryNeeds,
    travelling_with_kids: value.preferences.travellingWithKids,
    accessibility_needs: value.preferences.accessibilityNeeds,
  },
  notifications: {
    ...value.notifications,
    price_drop_alerts: value.notifications.priceDropAlerts,
    booking_reminders: value.notifications.bookingReminders,
    weekly_inspiration: value.notifications.weeklyInspiration,
    product_updates: value.notifications.productUpdates,
  },
});

type ProfileContextValue = {
    profile: ProfileState;
  suggestions: Record<string, unknown>[];
  profileLoading: boolean;
  profileSaving: boolean;
  profileError: string | null;
    updateProfile: (patch: Partial<Pick<ProfileState, 'displayName' | 'bio' | 'homeCity'>>) => void;
    updatePreferences: (patch: Partial<TravelPreferences>) => void;
    togglePreferenceItem: (
        key: 'preferredAirlines' | 'avoidedAirlines' | 'hotelAmenities' | 'propertyTypes' | 'favoriteDestinations' | 'interests' | 'dietaryNeeds',
        value: string,
    ) => void;
    updateNotifications: (patch: Partial<NotificationSettings>) => void;
    addBooking: (booking: Omit<Booking, 'id' | 'userId'>) => void;
    removeBooking: (id: string) => void;
    saveTrip: (trip: Omit<SavedTrip, 'id' | 'savedAt' | 'userId'>) => void;
    removeSavedTrip: (id: string) => void;
    isTripSaved: (destination: string) => boolean;
    logActivity: (text: string, kind?: ActivityEntry['kind']) => void;
    resetProfile: () => void;
};

const ProfileContext = createContext<ProfileContextValue | null>(null);

export const ProfileProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, getIdTokenClaims, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [profile, setProfile] = useState<ProfileState>(EMPTY_PROFILE);
  const [suggestions, setSuggestions] = useState<Record<string, unknown>[]>([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const profileReady = useRef(false);
  const profileMutationVersion = useRef(0);
  const skipNextSave = useRef(false);
  const profileRef = useRef(profile);
  const persistQueue = useRef(Promise.resolve());
  profileRef.current = profile;

  const getSessionToken = useCallback(async (forceGatewayLogin = false): Promise<string> => {
    const stored = (localStorage.getItem(SESSION_TOKEN_KEY) || '').trim();
    if (stored && !forceGatewayLogin) return stored;
    // Never send an expired cached ID token: refresh it, or re-sign-in.
    const idToken = await getFreshIdToken({ getIdTokenClaims, getAccessTokenSilently, loginWithRedirect });
    return loginToGateway(idToken, process.env.REACT_APP_AUTH0_DOMAIN);
  }, [getIdTokenClaims, getAccessTokenSilently, loginWithRedirect]);

  const persistProfile = useCallback(async (snapshot: ProfileState) => {
    if (!isAuthenticated) return;
    const uid = user?.sub?.trim();
    if (!uid) {
      setProfileError('Cannot save profile before Auth0 user identity is available.');
      return;
    }
    setProfileSaving(true);
    try {
      const token = await getSessionToken();
      const authenticatedSnapshot: ProfileState = {
        ...snapshot,
        userId: uid,
        bookings: snapshot.bookings.map(booking => ({ ...booking, userId: uid })),
        savedTrips: snapshot.savedTrips.map(trip => ({ ...trip, userId: uid })),
      };
      const result = await executeProfilePlaybook(
        token,
        uid,
        toPlaybookProfile(authenticatedSnapshot),
        { readOnly: false },
      );
      if (process.env.NODE_ENV !== 'production') {
        console.groupCollapsed('[Profile] save response');
        console.log('executionId:', result.executionId);
        console.log('status:', result.status);
        console.log('saved bookings:', authenticatedSnapshot.bookings);
        console.log('extracted profile:', result.profile);
        console.log('raw execution:', result.executionResult);
        console.groupEnd();
      }
      // The playbook reads the merged Firestore document after saving. Use it
      // as the canonical state, but do not start another save for reconciliation.
      if (Object.keys(result.profile).length > 0) {
        skipNextSave.current = true;
        const hydratedProfile = hydrate(result.profile, uid);
        profileRef.current = hydratedProfile;
        setProfile(hydratedProfile);
      }
      setSuggestions(result.suggestions);
      setProfileError(null);
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : 'Profile could not be saved.');
    } finally {
      setProfileSaving(false);
    }
  }, [getSessionToken, isAuthenticated, user?.sub]);

  const queueProfileSave = useCallback((snapshot: ProfileState) => {
    persistQueue.current = persistQueue.current
      .catch(() => undefined)
      .then(() => persistProfile(snapshot));
  }, [persistProfile]);

  const commitProfile = useCallback((nextProfile: ProfileState) => {
    // Mutations such as completing a booking must not depend on the debounce
    // effect or on a successful login-time read before they reach the backend.
    profileRef.current = nextProfile;
    profileMutationVersion.current += 1;
    skipNextSave.current = true;
    setProfile(nextProfile);
    queueProfileSave(nextProfile);
  }, [queueProfileSave]);

  // A profile playbook execution is the login-time read. The playbook uses a
  // merge write, so an empty profile reads the existing Firestore document
  // without replacing any stored fields.
  useEffect(() => {
    if (!isAuthenticated || !user?.sub) {
      profileReady.current = false;
      setProfile(EMPTY_PROFILE);
      setSuggestions([]);
      setProfileError(null);
      return;
    }

    let cancelled = false;
    const mutationVersionAtLoadStart = profileMutationVersion.current;
    profileReady.current = false;
    setProfile(EMPTY_PROFILE);
    setSuggestions([]);
    setProfileLoading(true);
    setProfileError(null);
    (async () => {
      try {
        const token = await getSessionToken(true);
        const result = await executeProfilePlaybook(token, user.sub!, {}, { readOnly: true });
        if (cancelled) return;
        if (process.env.NODE_ENV !== 'production') {
          console.groupCollapsed('[Profile] load response');
          console.log('executionId:', result.executionId);
          console.log('status:', result.status);
          console.log('extracted profile:', result.profile);
          console.log('suggestions:', result.suggestions);
          console.log('raw execution:', result.executionResult);
          console.groupEnd();
        }
        // A booking confirmed during this read must not be replaced by stale data.
        if (profileMutationVersion.current !== mutationVersionAtLoadStart) {
          profileReady.current = true;
          return;
        }
        skipNextSave.current = true;
        const hydratedProfile = hydrate(result.profile, user.sub);
        if (process.env.NODE_ENV !== 'production') {
          console.log('[Profile] hydrated load state:', hydratedProfile);
        }
        profileRef.current = hydratedProfile;
        setProfile(hydratedProfile);
        setSuggestions(result.suggestions);
        profileReady.current = true;
      } catch (error) {
        if (cancelled) return;
        setProfileError(error instanceof Error ? error.message : 'Profile could not be loaded.');
        // A failed read must not prevent a new booking or profile edit from
        // being saved. The next mutation sends the complete current profile.
        profileReady.current = true;
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getSessionToken, isAuthenticated, user?.sub]);

  // Persist edits through the profile playbook. Debouncing keeps a preference
  // picker from starting one execution per clicked option.
  useEffect(() => {
    if (!profileReady.current || !isAuthenticated || !user?.sub) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      queueProfileSave(profile);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated, profile, queueProfileSave, user?.sub]);

    const logActivity = useCallback((text: string, kind: ActivityEntry['kind'] = 'account') => {
        setProfile(prev => ({
            ...prev,
            activity: [{ id: newId('act'), text, at: new Date().toISOString(), kind }, ...prev.activity].slice(0, MAX_ACTIVITY),
        }));
    }, []);

    const updateProfile = useCallback<ProfileContextValue['updateProfile']>(patch => {
        setProfile(prev => ({ ...prev, ...patch }));
    }, []);

    const updatePreferences = useCallback<ProfileContextValue['updatePreferences']>(patch => {
        setProfile(prev => ({ ...prev, preferences: { ...prev.preferences, ...patch } }));
    }, []);

    const togglePreferenceItem = useCallback<ProfileContextValue['togglePreferenceItem']>((key, value) => {
        setProfile(prev => {
            const current = prev.preferences[key];
            const next = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
            return { ...prev, preferences: { ...prev.preferences, [key]: next } };
        });
    }, []);

    const updateNotifications = useCallback<ProfileContextValue['updateNotifications']>(patch => {
        setProfile(prev => ({ ...prev, notifications: { ...prev.notifications, ...patch } }));
    }, []);

    const addBooking = useCallback<ProfileContextValue['addBooking']>(booking => {
        const prev = profileRef.current;
        const entry: Booking = { ...booking, id: newId('bk'), userId: user?.sub || prev.userId };
        commitProfile({
            ...prev,
            bookings: [entry, ...prev.bookings],
            activity: [
                { id: newId('act'), text: `Booked ${entry.title}`, at: new Date().toISOString(), kind: 'booking' as const },
                ...prev.activity,
            ].slice(0, MAX_ACTIVITY),
        });
    }, [commitProfile, user?.sub]);

    const removeBooking = useCallback<ProfileContextValue['removeBooking']>(id => {
        const prev = profileRef.current;
        commitProfile({ ...prev, bookings: prev.bookings.filter(b => b.id !== id) });
    }, [commitProfile]);

    const saveTrip = useCallback<ProfileContextValue['saveTrip']>(trip => {
        const prev = profileRef.current;
        if (prev.savedTrips.some(t => t.destination.toLowerCase() === trip.destination.toLowerCase())) return;
        const entry: SavedTrip = { ...trip, id: newId('trip'), userId: prev.userId, savedAt: new Date().toISOString() };
        commitProfile({
            ...prev,
            savedTrips: [entry, ...prev.savedTrips],
            activity: [
                { id: newId('act'), text: `Saved ${entry.destination}`, at: new Date().toISOString(), kind: 'save' as const },
                ...prev.activity,
            ].slice(0, MAX_ACTIVITY),
        });
    }, [commitProfile]);

    const removeSavedTrip = useCallback<ProfileContextValue['removeSavedTrip']>(id => {
        const prev = profileRef.current;
        commitProfile({ ...prev, savedTrips: prev.savedTrips.filter(t => t.id !== id) });
    }, [commitProfile]);

    const isTripSaved = useCallback(
        (destination: string) =>
            profile.savedTrips.some(t => t.destination.toLowerCase() === destination.toLowerCase()),
        [profile.savedTrips],
    );

    const resetProfile = useCallback(() => {
        // The server is now authoritative; the debounced persistence effect
        // sends the reset state to the profile playbook for this Auth0 user.
        setProfile(prev => ({ ...EMPTY_PROFILE, userId: prev.userId }));
    }, []);

    const value = useMemo<ProfileContextValue>(() => ({
        profile,
        suggestions,
        profileLoading,
        profileSaving,
        profileError,
        updateProfile,
        updatePreferences,
        togglePreferenceItem,
        updateNotifications,
        addBooking,
        removeBooking,
        saveTrip,
        removeSavedTrip,
        isTripSaved,
        logActivity,
        resetProfile,
    }), [
        profile, suggestions, profileLoading, profileSaving, profileError,
        updateProfile, updatePreferences, togglePreferenceItem, updateNotifications,
        addBooking, removeBooking, saveTrip, removeSavedTrip, isTripSaved, logActivity, resetProfile,
    ]);

    return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = (): ProfileContextValue => {
    const ctx = useContext(ProfileContext);
    if (!ctx) throw new Error('useProfile must be used inside a ProfileProvider');
    return ctx;
};
